import React, { useState, useCallback, useEffect } from "react";
import useStyles from "./style";
import {
  Button,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "@mui/material";
import {
  EditOutlined,
  DeleteOutline,
  AddOutlined,
  SearchOutlined,
  ChevronLeft,
  ChevronRight,
} from "@mui/icons-material";
import { showLoader, hideLoader } from "Redux/Loader/actions";
import { showSnackbar } from "Redux/SnackBar/actions";
import { useDispatch, useSelector } from "react-redux";
import _ from "lodash";

// Services
import tripPreferenceApi from "Services/TripPreference";

// Modals
import PreferenceAddModal from "Pages/Modals/TripPreferences/Create";
import PreferenceUpdateModal from "Pages/Modals/TripPreferences/Update";
import PreferenceDeleteDialog from "Pages/Modals/TripPreferences/Delete";

const TripPreferences = () => {
  const classes = useStyles();
  const dispatch = useDispatch();

  const demoaccount_id = useSelector(
    (state) => state?.AuthReducer?.demoaccount_id || ""
  );

  const [row, setRow] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [searchText, setSearchText] = useState("");

  // Pagination
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);

  // Modals
  const [addModal, setAddModal] = useState(false);
  const [updateModal, setUpdateModal] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);

  const handleOpenAddModal = () => {
    setAddModal(true);
  };

  const handleCloseAddModal = () => {
    setAddModal(false);
    getPreferencesList();
  };

  const handleOpenUpdateModal = (row) => {
    setSelectedId(row?._id);
    setUpdateModal(true);
  };

  const handleCloseUpdateModal = () => {
    setUpdateModal(false);
    getPreferencesList();
  };

  const handleOpenDeleteModal = (row) => {
    setSelectedId(row?._id);
    setDeleteModal(true);
  };

  const handleCloseDeleteModal = () => {
    setDeleteModal(false);
    getPreferencesList();
  };

  // Get Trip Preferences list API
  const getPreferencesList = useCallback(() => {
    dispatch(showLoader("Loading please wait..."));
    const onSuccess = (res) => {
      dispatch(hideLoader());
      if (res?.data?.status === true) {
        setTotalPages(res?.data?.data?.totalRecords || 1);
        setRow(res?.data?.data?.data || []);
      } else {
        setRow([]);
      }
    };
    const onFailure = (err) => {
      dispatch(hideLoader());
      dispatch(
        showSnackbar({
          message: err?.response?.data?.message || "Failed to fetch data",
          autoHideDuration: 3000,
          anchorOrigin: {
            vertical: "top",
            horizontal: "right",
          },
          variant: "error",
        })
      );
    };
    const params = { page, limit: pageSize, search: searchText };
    tripPreferenceApi.getTripPreferencesList(params).then(onSuccess, onFailure);
  }, [page, pageSize, searchText, dispatch]);

  useEffect(() => {
    getPreferencesList();
  }, [getPreferencesList]);

  const calculateSno = (pageIndex, pSize, index) => {
    return (pageIndex - 1) * pSize + index + 1;
  };

  const dataRows = _.map(row || [], (data, index) => ({
    sno: calculateSno(page, pageSize, index),
    name: data?.name,
    icon: data?.icon,
    status: data?.status,
    row: data,
  }));

  const totalPagesCount = Math.ceil(totalPages / pageSize) || 1;

  const renderPagination = () => {
    let pages = [];
    const internalPage = page - 1;
    if (totalPagesCount <= 5) {
      for (let i = 0; i < totalPagesCount; i++) pages.push(i);
    } else {
      if (internalPage < 3) {
        pages = [0, 1, 2, "...", totalPagesCount - 1];
      } else if (internalPage > totalPagesCount - 4) {
        pages = [0, "...", totalPagesCount - 3, totalPagesCount - 2, totalPagesCount - 1];
      } else {
        pages = [0, "...", internalPage - 1, internalPage, internalPage + 1, "...", totalPagesCount - 1];
      }
    }

    return pages.map((item, index) => {
      if (item === "...") {
        return (
          <span key={index} className={classes.pageDots}>
            ...
          </span>
        );
      }
      return (
        <Button
          key={item}
          className={internalPage === item ? classes.pageButtonActive : classes.pageButton}
          onClick={() => setPage(item + 1)}
        >
          {item + 1}
        </Button>
      );
    });
  };

  return (
    <Box className={classes.root}>
      {/* Header */}
      <Box className={classes.headerContainer}>
        <Typography className={classes.pageTitle}>Trip Preferences</Typography>
        <Typography className={classes.breadcrumb}>
          Admin / Fare Management / <span>Trip Preferences</span>
        </Typography>
      </Box>

      {/* Action Row */}
      <Box className={classes.actionRow}>
        {/* Search */}
        <Box className={classes.searchWrapper}>
          <SearchOutlined className={classes.searchIcon} />
          <input
            type="text"
            placeholder="Search preferences..."
            className={classes.searchInput}
            value={searchText}
            onChange={(e) => {
              setSearchText(e.target.value);
              setPage(1);
            }}
          />
        </Box>

        {/* Right Actions */}
        <Box className={classes.rightActions}>
          {!demoaccount_id && (
            <Button
              variant="contained"
              className={classes.addButton}
              onClick={handleOpenAddModal}
              startIcon={<AddOutlined />}
            >
              Add New Preference
            </Button>
          )}
        </Box>
      </Box>

      {/* Table */}
      <TableContainer component={Paper} elevation={0} className={classes.tableWrapper}>
        <Table>
          <TableHead>
            <TableRow className={classes.tableHeaderRow}>
              <TableCell className={classes.tableHeaderCell}>S.No</TableCell>
              <TableCell className={classes.tableHeaderCell}>Icon</TableCell>
              <TableCell className={classes.tableHeaderCell}>Name</TableCell>
              <TableCell className={classes.tableHeaderCell}>Status</TableCell>
              {!demoaccount_id && (
                <TableCell className={classes.tableHeaderCell} align="right">Actions</TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {dataRows.map((rowItem) => (
              <TableRow key={rowItem.sno} className={classes.tableRow}>
                <TableCell className={classes.tableCell}>{rowItem.sno}</TableCell>
                <TableCell className={classes.tableCell}>
                  {rowItem.icon ? (
                    <Box
                      component="img"
                      sx={{
                        width: 36,
                        height: 36,
                        borderRadius: "6px",
                        objectFit: "contain",
                        border: "1px solid #e2e8f0",
                        padding: "2px",
                        boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
                      }}
                      src={rowItem.icon}
                      alt={rowItem.name}
                    />
                  ) : (
                    "-"
                  )}
                </TableCell>
                <TableCell className={`${classes.tableCell} ${classes.boldText}`}>
                  {rowItem.name}
                </TableCell>
                <TableCell className={classes.tableCell}>
                  {rowItem.status === 1 ? (
                    <span className={classes.statusActive}>Active</span>
                  ) : (
                    <span className={classes.statusInactive}>Inactive</span>
                  )}
                </TableCell>
                {!demoaccount_id && (
                  <TableCell className={classes.tableCell} align="right">
                    <Box
                      component="span"
                      className={classes.actionButton}
                      onClick={() => handleOpenUpdateModal(rowItem.row)}
                    >
                      <EditOutlined fontSize="small" />
                      Edit
                    </Box>
                    <Box
                      component="span"
                      className={classes.actionDeleteButton}
                      onClick={() => handleOpenDeleteModal(rowItem.row)}
                    >
                      <DeleteOutline fontSize="small" />
                      Delete
                    </Box>
                  </TableCell>
                )}
              </TableRow>
            ))}
            {dataRows.length === 0 && (
              <TableRow>
                <TableCell colSpan={demoaccount_id ? 4 : 5} style={{ textAlign: "center", padding: "40px" }}>
                  <Typography variant="body2" color="textSecondary">
                    No trip preferences found
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>

        {/* Pagination */}
        <Box className={classes.paginationContainer}>
          <Typography className={classes.paginationText}>
            Showing {(page - 1) * pageSize + (dataRows.length > 0 ? 1 : 0)} to{" "}
            {Math.min(page * pageSize, totalPages)} of {totalPages} results
          </Typography>

          <Box className={classes.paginationControls}>
            <ChevronLeft
              className={`${classes.pageArrow} ${page === 1 ? "disabled" : ""}`}
              onClick={() => page > 1 && setPage(page - 1)}
            />
            {renderPagination()}
            <ChevronRight
              className={`${classes.pageArrow} ${page === totalPagesCount ? "disabled" : ""}`}
              onClick={() => page < totalPagesCount && setPage(page + 1)}
            />
          </Box>
        </Box>
      </TableContainer>

      {/* Add Modal */}
      <PreferenceAddModal
        openDialog={addModal}
        handleClose={handleCloseAddModal}
      />

      {/* Update Modal */}
      <PreferenceUpdateModal
        openDialog={updateModal}
        id={selectedId}
        handleClose={handleCloseUpdateModal}
      />

      {/* Delete Dialog */}
      <PreferenceDeleteDialog
        openDialog={deleteModal}
        id={selectedId}
        handleClose={handleCloseDeleteModal}
      />
    </Box>
  );
};

export default TripPreferences;
